import { Receipt } from "lucide-react";

/**
 * Compact, brand-compliant invoice payment summary.
 *
 *   Total                $500.00
 *   Deposit paid          $250.00   (orange)
 *   Balance due           $250.00   (highlight)
 *   [partially paid / paid / awaiting badge]
 *
 * Props:
 *   summary: {
 *     invoice_number?: string,
 *     total: number,
 *     amount_paid?: number,
 *     balance_due?: number,
 *     payment_type?: "full" | "split",
 *     deposit_required?: number | null,
 *     status?: "draft"|"sent"|"partially_paid"|"paid"|"void",
 *   } | null
 *
 *   theme: "dark" (navy bg, white text — for Tech app)
 *        | "light" (white bg, navy text — for CRM + customer Track)
 */
export default function PaymentSummary({ summary, theme = "light", testid = "payment-summary" }) {
  if (!summary || (!summary.total && !summary.amount_paid)) return null;
  const total = Number(summary.total || 0);
  const paid = Number(summary.amount_paid || 0);
  const balance = Number(summary.balance_due ?? total - paid);
  const isSplit = (summary.payment_type || "full") === "split";
  const depositReq = Number(summary.deposit_required || 0);
  const fullyPaid = balance <= 0.01 && total > 0;
  const partial = paid > 0 && !fullyPaid;
  const awaiting = paid === 0 && total > 0;

  // Theme styling
  const dark = theme === "dark";
  const bg = dark ? "bg-[#002060]" : "bg-[#FFFFFF]";
  const border = dark ? "border-[#FF6600]/40" : "border-[#002060]/15";
  const label = dark ? "text-[#FFFFFF]/70" : "text-[#002060]/70";
  const labelBold = dark ? "text-[#FFFFFF]" : "text-[#002060]";
  const value = dark ? "text-[#FFFFFF]" : "text-[#002060]";
  const accent = "text-[#FF6600]";
  const headerLabel = dark ? "text-[#FF6600]" : "text-[#FF6600]";

  // Status badge
  const badge = fullyPaid
    ? { label: "Paid in full", classes: "bg-[#FF6600] text-[#FFFFFF] border-[#FF6600]" }
    : partial
      ? { label: "Partial payment — balance due", classes: `border-[#FF6600] ${dark ? "bg-[#FF6600]/15 text-[#FF6600]" : "bg-[#FF6600]/10 text-[#FF6600]"}` }
      : awaiting
        ? { label: depositReq > 0 ? `Deposit $${depositReq.toFixed(2)} due` : "Awaiting payment", classes: dark ? "bg-[#FFFFFF]/10 text-[#FFFFFF] border-[#FFFFFF]/30" : "bg-[#002060]/5 text-[#002060] border-[#002060]/20" }
        : null;

  return (
    <div
      data-testid={testid}
      className={`${bg} border-2 ${border} rounded-lg p-4 space-y-2`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className={`w-3.5 h-3.5 ${accent}`} />
          <span className={`text-[10px] uppercase tracking-[0.25em] font-bold ${headerLabel}`}>
            Invoice payment
          </span>
        </div>
        {summary.invoice_number && (
          <span className={`text-[11px] num ${label}`} data-testid={`${testid}-invoice-number`}>
            {summary.invoice_number}
          </span>
        )}
      </div>

      <div className="space-y-1 text-sm">
        <Row label="Total" labelClass={label} valueClass={`num font-semibold ${value}`} value={`$${total.toFixed(2)}`} testid={`${testid}-total`} />
        {paid > 0 && (
          <Row
            label={isSplit && depositReq > 0 ? "Deposit paid" : "Paid"}
            labelClass={label}
            valueClass={`num font-semibold ${accent}`}
            value={`$${paid.toFixed(2)}`}
            testid={`${testid}-paid`}
          />
        )}
        {!fullyPaid && (
          <Row
            label="Balance due"
            labelClass={`font-semibold ${labelBold}`}
            valueClass={`num font-bold text-base ${accent}`}
            value={`$${Math.max(balance, 0).toFixed(2)}`}
            testid={`${testid}-balance`}
          />
        )}
        {fullyPaid && (
          <Row
            label="Balance"
            labelClass={label}
            valueClass={`num font-semibold ${value}`}
            value="$0.00"
            testid={`${testid}-balance`}
          />
        )}
      </div>

      {badge && (
        <div
          className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${badge.classes}`}
          data-testid={`${testid}-status`}
        >
          {badge.label}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, labelClass, valueClass, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={labelClass}>{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
